import React from 'react';
import { Switch, Checkbox, Date, File, Number, Select, Text, TextArea, List } from './FormInputs';
import { Grid, ExpansionPanel, ExpansionPanelSummary, ExpansionPanelDetails } from '@material-ui/core';
import Custom from './FormInputs/Custom';

/**
 * @func checkInputValidity
 * @desc check the input against its validity functions
 */
export const checkInputValidity = (inputObj, inputData, parentDataObj = null) => {
  // reset errors
  inputObj.error = false;
  inputObj.errorMessage = null;

  let errorMessage = undefined;

  if (inputObj.validate && inputObj.validate.length) {
    // run through the validation functions for this input
    errorMessage = inputObj.validate.reduce((acc, func) => (acc ? acc : func(inputData, parentDataObj)), undefined);
  }

  // // 'undefined' means that the input is valid
  // if (errorMessage !== undefined) {
  //   throw new Error(errorMessage);
  // }
  return errorMessage; // an error string or null
};

/**
 * @func validateInputs
 * @desc checks for invalid inputs
 * @param inputArr array of input objects
 * @param data data object for this nested level
 * @param errorObj accumulator object for all errors
 */
export const validateInputs = (inputs, data, errorObj = {}) => {
  inputs.forEach((input, inputIdx) => {
    try {
      const { field, inputType, children, child } = input;

      if (['array', 'list'].includes(inputType) && Array.isArray(data[field])) {
        errorObj[field] = data[field].map((d, dIdx) => validateInputs(children, data[field][dIdx], errorObj[field]));
      }

      if (inputType === 'object' && ![null, undefined].includes(data[field]) && typeof data[field] === 'object') {
        const existingObj = Object.assign({}, errorObj[field]);
        const newObj = validateInputs([child], data[field], errorObj[field]);
        errorObj[field] = Object.assign({}, existingObj, newObj);
      }

      if (['gridcontainer', 'griditem'].includes(inputType)) {
        errorObj = validateInputs(children, data, errorObj);
      }

      if (!['array', 'list', 'object', 'gridcontainer', 'griditem'].includes(inputType)) {
        const errorMessage = checkInputValidity(input, data[field], data);
        errorObj[field] = errorMessage;
      }
    } catch (error) {
      //
    }
  });
  return errorObj;
};

// /**
//  * @func validateForm
//  * @desc validates the entire form using an array of validation functions provided in the input maps
//  * @returns null if valid, array of errors if not valid
//  */
// export const validateForm = () => {
//   // get errors and data and search them recuirsively
//   // formHasError = false;

//   return checkErrors(formInputs, formData);

//   // const tempErrors = checkErrors(formInputs, formData);
//   // setFormErrors(tempErrors);

//   // if (formHasError) {
//   //   setSubmitError('There are form errors... Please change them and try submitting again.');
//   // }
//   // return !formHasError; // false means the form is not valid
// };

/**
 * @func flattenObject
 * @desc flatten any object with nested properties using a double underscore to denote a parent to child relationship or an array index value
 * @param {*} obj
 * @param { string } treeTraceStr
 */
export const flattenObject = (obj, treeTraceStr = '') => {
  const flattened = {};

  Object.keys(obj).forEach(key => {
    if (typeof obj[key] === 'object' && obj[key] !== null) {
      Object.assign(flattened, flattenObject(obj[key], `${treeTraceStr}${!!treeTraceStr.length ? '__' : ''}${key}`));
    } else {
      flattened[`${treeTraceStr}__${key}`] = obj[key];
    }
  });

  return flattened;
};

/**
 * @func createErrorObject
 * @desc creates an object to hold all errors for inputs
 * @desc iterates over the supplied inputs and creates an error object
 *      - uses the data in case there is an array and needs to make an array of errors objects for a field
 */
export const createErrorObject = (inputs, data, errorObj = {}) => {
  // console.log(inputs, data, errorObj);
  inputs.forEach(input => {
    const { field, inputType, children, child } = input;

    if (['array', 'list'].includes(inputType) && Array.isArray(data[field])) {
      errorObj[field] = data[field].length
        ? data[field].map(d => createErrorObject(children, data[field]))
        : [createEmptyDataObject(children)];
      // return;
    }

    if (inputType === 'object' && ![null, undefined].includes(data[field]) && typeof data[field] === 'object') {
      const existingObj = Object.assign({}, errorObj[field]);
      const newObj = createErrorObject([child], data[field], errorObj[field]);
      errorObj[field] = Object.assign({}, existingObj, newObj);
    }

    if (['gridcontainer', 'griditem'].includes(inputType)) {
      errorObj = createErrorObject(children, data, errorObj);
    }

    if (!['array', 'list', 'object', 'gridcontainer', 'griditem'].includes(inputType)) {
      errorObj[field] = null;
      // return;
    }
  });

  return errorObj;
};

export const createTreeTraceBranch = (field, index) => {
  return {
    field,
    index,
  };
};

/**
 * @func createEmptyDataObject
 * @desc creates an empty data object from the inputs given (used when in CREATE mode)
 */
export const createEmptyDataObject = inputs => {
  let dataObj = {};

  inputs.forEach(input => {
    const { field, inputType, children, child } = input;

    if (['array', 'list'].includes(inputType)) {
      dataObj[field] = [createEmptyDataObject(children)];
    } else if (inputType === 'object') {
      dataObj[field] = createEmptyDataObject([child]);
    } else if (['boolean', 'switch'].includes(inputType)) {
      dataObj[field] = false;
    } else if (inputType === 'checkbox') {
      dataObj[field] = [];
    } else if (['gridcontainer', 'griditem'].includes(inputType)) {
      dataObj = createEmptyDataObject(children);
    } else {
      dataObj[field] = '';
    }
  });
  return dataObj;
};

const defaultGridItemSizes = {
  xs: 12,
  sm: 6,
  md: 6,
  lg: 6,
  xl: 6,
};

export const renderInput = (
  {
    i,
    treeTrace = [],
    errors,
    data,
    extraIndexForKey,
    mode,
    onChange,
    numberOfChildren,
    isListItem = false,
    resetErrors,
    addListItem,
  },
  showLogs = false,
) => {
  // console.log(i);

  const inputProps = {
    i,
    treeTrace,
    errors: errors && errors[i.field] ? errors[i.field] : errors,
    error: errors && errors[i.field],
    data,
    extraIndexForKey,
    mode,
    onChange,
    numberOfChildren,
    isListItem,
    showLogs,
    resetErrors,
    addListItem,
  };

  let value = typeof data === 'object' ? data[i.field] : data;
  // let value = data[i.field]

  // showLogs && console.log(inputProps);

  // if the `hideOnCreate` flag is true and the form is in CREATE mode, don't show the input
  if ((i.hideOnCreate && mode === 'CREATE') || (i.hideOnEdit && mode === 'EDIT')) {
    return <></>;
  }

  switch (i.inputType) {
    case 'text':
      // showLogs && console.log(data);
      return (
        <Text
          {...inputProps}
          value={value}
          key={treeTrace
            .join('_')
            .concat(i.field)
            .concat(extraIndexForKey || 0)}
          showLogs={showLogs}
        />
      );
    case 'phone':
      const phonePattern = /^(\d{3})(\d{3})(\d{3})$/;
      return (
        <Text
          {...inputProps}
          value={value?.length === 9 ? value.replace(phonePattern, '$1-$2-$3') : value}
          key={treeTrace
            .join('_')
            .concat(i.field)
            .concat(extraIndexForKey || 0)}
          showLogs={showLogs}
        />
      );

    case 'date':
      return <Date {...inputProps} value={value} />;

    case 'password':
      return (
        <Text
          {...inputProps}
          isPassword
          value={value}
          key={treeTrace
            .join('_')
            .concat(i.field)
            .concat(extraIndexForKey)}
        />
      );

    case 'textarea':
      return <TextArea {...inputProps} value={value} />;

    case 'number':
      return <Number {...inputProps} value={value} />;

    case 'boolean':
    case 'switch':
      return <Switch {...inputProps} value={value} />;

    case 'select':
      return <Select {...inputProps} value={value} onChange={onChange} />;

    case 'checkbox':
      return <Checkbox {...inputProps} value={value} />;

    case 'file':
      return <File {...inputProps} value={value} />;

    case 'custom':
      return <Custom {...inputProps} value={value} />;

    case 'object':
      return (
        <div
          key={treeTrace
            .join('_')
            .concat(i.field)
            .concat(extraIndexForKey)}
          style={{ width: '100%' }}
        >
          {data && data[i.field] && (
            <div>
              {renderInput({
                ...inputProps,
                i: i.child,
                treeTrace: treeTrace.concat(createTreeTraceBranch(i.field, null)),
                errors: errors[i.field],
                data: data[i.field],
              })}
            </div>
          )}
        </div>
      );

    case 'array':
      // check to make sure the data exists and there is a field for it in 'errors'
      if (!data || !data[i.field] || !errors[i.field]) {
        return <></>;
        break;
      }

      // make it render at least once so the inputs are not hidden (create an empty object for the array)
      if (!data[i.field].length) {
        // data[i.field] = [createEmptyDataObject(i.children)];
      }

      // console.log(i)

      return (
        // <div>
        <List
          key={'array_' + treeTrace.join('_').concat(i.field)}
          // className={classes.inputContainer}
          label={i.label}
          value={data[i.field]}
          {...inputProps}
          // function overrides
          removeListItem={index => {
            // remove the item from the existing data set
            const tempData = data[i.field].slice(0, index).concat(data[i.field].slice(index + 1, data[i.field].length));
            onChange(i.field, tempData, treeTrace, i.onChange, index);
            resetErrors();
          }}
          addListItem={() => addListItem(i, treeTrace, data[i.field])}
        >
          {data[i.field].map((entry, entryIndex) => (
            <div
              key={treeTrace
                .join('_')
                .concat(entry.field)
                .concat(extraIndexForKey)}
              style={{ width: '100%' }}
            >
              {i &&
                i.children &&
                i.children.map(childInput =>
                  renderInput(
                    {
                      ...inputProps,
                      i: childInput,
                      treeTrace: treeTrace.concat(createTreeTraceBranch(i.field, entryIndex)),
                      errors: errors[i.field][entryIndex],
                      data: entry,
                      isListItem: true,
                    },
                    true,
                  ),
                )}
            </div>
          ))}
          {/* </div> */}
        </List>
      );

    case 'gridcontainer':
      return (
        <Grid
          container
          // className={classes.gridContainer}
          style={{
            marginBottom: 4,
            borderRadius: 5,
          }}
          spacing={4}
          key={`grid-${treeTrace.join('-')}-${extraIndexForKey}`}
          {...i.gridProps}
        >
          {i.children.map((c, cIdx) =>
            renderInput({
              ...inputProps,
              i: c,
              extraIndexForKey: cIdx,
              numberOfChildren: i.children.length,
            }),
          )}
        </Grid>
      );

    case 'griditem':
      const { xs, sm, md, lg, xl } = inputProps;
      const sizeOverrides = Object.entries({
        xs,
        sm,
        md,
        lg,
        xl,
      })
        .filter(pair => pair[1])
        .reduce((acc, cur) => {
          acc[cur[0]] = cur[1];
          return acc;
        }, {});
      const sizes = Object.assign({}, defaultGridItemSizes, sizeOverrides);

      // console.log(sizeOverrides);
      // console.log(sizes);

      return (
        <Grid
          item
          {...sizes}
          // className={classes.gridItem}
          style={{ padding: '0 16px 16px 16px' }}
          key={`grid-item-${treeTrace.join('-')}-${extraIndexForKey}`}
          {...i.gridProps}
        >
          {i.children.map((c, cIdx) =>
            renderInput({
              ...inputProps,
              i: c,
              extraIndexForKey: cIdx,
            }),
          )}
        </Grid>
      );

    default:
      return <div />;
  }
};
